import emrService from '@/apis/emr/service';
import BaseTable from '@/components/bases/BaseTable';
import constants from '@/constants';
import emrTypeHelper from '@/helper/emrType';
import EmrRangePicker from '@/screens/CreateEmr/EmrInput/EmrRangePicker';
import EmrSearch from '@/screens/CreateEmr/EmrInput/EmrSearch';
import EmrSelect from '@/screens/CreateEmr/EmrInput/EmrSelect';
import DetailContent from '@/screens/Timeline/components/DetailContent';
import useGlobalConfigs from '@/stores/globalConfigs';
import { usePhrStoreInContext } from '@/stores/phr';
import quickModal from '@/utils/quickModal';
import { Button, Col, Drawer, Flex, Row, Tag } from 'antd';
import dayjs from 'dayjs';
import _ from 'lodash';
import * as React from 'react';

export interface IFamilyHistoryProps {}

interface IFilter {
  id?: string[];
  icd?: string;
  group?: string;
  //   filter?: {
  //     'data.icd.data.icd.value'?: string;
  //     'data.icd.data.group'?: string;
  //   };
  start?: dayjs.Dayjs;
  end?: dayjs.Dayjs;
}

interface IEMRDataWithRelation extends IEMRData {
  relation: IRelation;
}

const FamilyHistory: React.FC<IFamilyHistoryProps> = ({ ...props }) => {
  const [openFilter, setOpenFilter] = React.useState<boolean>(false);
  const emrTypes = useGlobalConfigs((s) => s.emrTypes);
  const user = usePhrStoreInContext((s) => s.user);
  const relationsWithMe = user?.family.relationsWithMe || {};
  const members = [...(user?.family.members || []), user?.family.protector];
  const idMembers = members
    .map((m) => m?._id as string)
    .filter((m) => {
      if (m === user?._id) return false;
      return Boolean(m);
    });

  const [filter, setFilter] = React.useState<IFilter>({ id: [] });

  const condition = {
    uid: (filter.id?.length ? filter.id : idMembers)?.join(',') || idMembers.join(',') || '',
    type: 'healthRecord',
    limit: 20,
    filter: JSON.stringify(
      _.pickBy(
        {
          'data.icd.data.icd.value': filter.icd,
          'data.icd.data.group': filter.group,
          'data.icd.data.family': true,
        },
        _.identity,
      ),
    ),
    start: filter.start?.startOf('date').toDate(),
    end: filter.start?.endOf('date').toDate(),
  };

  const { data, fetchNextPage, isFetchingNextPage, refetch } = emrService.useGetEmrInfinite(condition);

  const healthRecordEmrType = emrTypes?.['healthRecord'];
  const icdEmrTypeConfig = emrTypes?.['icd'];
  const fieldGroupConfig = icdEmrTypeConfig?.format?.fields?.find((f) => f.key === 'group');
  const objRawDataSource: Record<string, IEMRData> = {};

  const dataSource = data?.pages
    ?.reduce((d: IEMRDataWithRelation[], e: any) => {
      e?.data?.forEach((emr: IEMRDataWithRelation) => {
        emr.relation = relationsWithMe[emr.user._id];
        objRawDataSource[emr._id] = emr;
        if (emr?.data?.icd?.length) {
          emr?.data?.icd?.forEach((icd: any) => {
            if (
              // Lọc theo icd
              (!filter.icd || icd?.data?.icd?.value === filter.icd) &&
              // Lọc theo nhóm bệnh
              (!filter.group || icd?.data?.group === filter.group)
            ) {
              const emrClone = _.cloneDeep(emr);
              emrClone.data.icd = [icd];
              d.push(emrClone);
            }
          });
        } else {
          d.push(emr);
        }
      });
      return d;
    }, [])
    .sort((a, b) => (a?.relation?.value < b?.relation?.value ? -1 : 1));

  return (
    <>
      <Drawer
        height={'auto'}
        placement="top"
        open={openFilter}
        title={
          <div className="flex justify-between items-center">
            <div>Bộ lọc Tiền sử gia đình</div>
            <Flex gap={8}>
              <Button
                danger
                onClick={() => {
                  setOpenFilter(false);
                  setFilter({});
                }}
              >
                Hủy lọc
              </Button>
              <Button onClick={() => setOpenFilter(false)}>Đóng</Button>
            </Flex>
          </div>
        }
        onClose={() => {
          setOpenFilter(false);
        }}
      >
        <Row gutter={8}>
          <Col span={12}>
            <EmrSelect
              allowClear
              labelProps={{ name: 'group', required: false }}
              field={icdEmrTypeConfig?.format.fields?.find((f) => f.key === 'group')}
              value={filter?.group}
              onChange={(v) => setFilter((s) => ({ ...s, group: v }))}
            />
          </Col>
          <Col span={12}>
            <EmrSearch
              allowClear
              labelProps={{ label: 'Tìm theo ICD', name: 'icd', required: false }}
              key={'icd'}
              field={icdEmrTypeConfig?.format.fields?.find((f) => f.key === 'icd')}
              value={filter?.icd}
              onChange={(v) => setFilter((s) => ({ ...s, icd: v }))}
            />
          </Col>
          <Col span={12}>
            <EmrSelect
              allowClear
              mode="tags"
              labelProps={{ label: 'Tìm theo thành viên', name: 'id', required: false }}
              options={Object.values(relationsWithMe).map((r) => {
                return {
                  label: `${r.username} (${r.value})`,
                  value: r.destination,
                };
              })}
              value={filter?.id}
              onChange={(v) => setFilter((s) => ({ ...s, id: v }))}
            />
          </Col>
          <Col span={12}>
            <EmrRangePicker
              labelProps={{ label: 'Chọn khoảng thời gian', name: 'dateRange', required: false }}
              rangePickerProps={{
                allowClear: true,
                format: constants.DATE_FORMAT,
                value: [filter?.start, filter?.end],
                onChange: (values) =>
                  setFilter((s) => ({ ...s, start: values?.[0] as dayjs.Dayjs, end: values?.[1] as dayjs.Dayjs })),
              }}
            />
          </Col>
        </Row>
      </Drawer>
      <div className="px-2 pb-2 flex justify-end">
        <Button
          className=""
          onClick={() => {
            setOpenFilter(true);
          }}
        >
          Bộ lọc
        </Button>
      </div>
      <div
        className="max-h-[600px] overflow-y-auto px-2"
        onScroll={(e) => {
          const element: any = e.target;
          const { scrollTop, clientHeight, scrollHeight } = element;
          if (scrollTop + clientHeight >= scrollHeight - 50) {
            !isFetchingNextPage && fetchNextPage();
          }
        }}
      >
        <BaseTable
          loading={isFetchingNextPage}
          pagination={false}
          dataSource={dataSource}
          columns={[
            {
              key: 'relation',
              title: 'MQH',
              render(_, record: IEMRDataWithRelation) {
                // console.log(333, record.user._id);
                // const relation = relationsWithMe?.find((r) => r.destination === record.user._id);
                return record?.relation?.value;
              },
            },
            {
              key: 'name',
              title: 'Tên',
              render(_, record: IEMRData) {
                return record.user?.username;
              },
            },
            {
              key: 'group',
              title: 'Nhóm bệnh',
              render(_, record: IEMRData) {
                return record?.data?.icd?.map((d: IEMRData, i: number) => {
                  const detailValue = emrTypeHelper.getOption(fieldGroupConfig as IFieldConfig, d?.data.group);
                  return detailValue ? (
                    <div key={i.toString()} className="p-1">
                      <Tag className="rounded-3xl w-full flex justify-center" color={detailValue?.color}>
                        {detailValue?.label}
                      </Tag>
                    </div>
                  ) : (
                    ''
                  );
                });
              },
            },
            {
              key: 'date',
              title: 'Thời gian',
              dataIndex: 'date',
              render(value) {
                return <>{value ? dayjs(value).format(constants.DATE_FORMAT) : ''}</>;
              },
            },
            {
              key: 'detail',
              title: 'Chẩn đoán',
              render(_, record: IEMRData) {
                return record?.data?.icd
                  ?.map((d: IEMRData) => {
                    return d?.data.definiteDiagnosis;
                  })
                  ?.join(', ');
              },
            },
            {
              key: 'icd',
              title: 'Mã ICD',
              render(_, record: IEMRData) {
                return record?.data?.icd
                  ?.map((d: IEMRData) => {
                    return d?.data?.icd?.label;
                  })
                  ?.join(', ');
              },
            },
            {
              key: 'healthRecord',
              title: 'Phiếu khám',
              render(_, record: IEMRData) {
                return (
                  <Button
                    onClick={() => {
                      quickModal.custom({
                        mask: true,
                        title: 'Hồ sơ bệnh án',
                        width: 900,
                        afterClose() {
                          refetch();
                        },
                        content: (
                          <DetailContent
                            emr={objRawDataSource?.[record._id]}
                            emrType={healthRecordEmrType as IEMRType}
                          />
                        ),
                      });
                    }}
                    className=""
                  >
                    {record?.data?.healthRecord || 'Phiếu khám'}
                  </Button>
                );
              },
            },
          ]}
        />
      </div>
    </>
  );
};

export default FamilyHistory;
